import * as React from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import { Save, Sparkles, Trash2, Plus, Palette, Image as ImageIcon, Lock } from "lucide-react";
import DevelopersSection from "./DevelopersSection";
import SecuritySection from "./SecuritySection";

const TERM_KEYS = [
  { k: "bookings", label: "Bookings (plural)" },
  { k: "booking",  label: "Booking (singular)" },
  { k: "sites",    label: "Sites (plural)" },
  { k: "site",     label: "Site (singular)" },
  { k: "members",  label: "Team (plural)" },
  { k: "member",   label: "Team member (singular)" },
  { k: "services", label: "Services (plural)" },
  { k: "service",  label: "Service (singular)" },
  { k: "customer", label: "Customer" },
];

const TAB_ORDER = ["general", "vertical", "terminology", "branding", "custom-fields", "security", "developers", "modules"];

export default function Settings() {
  const { companyId } = useParams();
  const { company, entitlements, catalog, has, refresh } = useCompany();
  const canCompany = has("company.manage");
  const canEnt = has("entitlements.manage");

  const [tab, setTab] = React.useState("general");
  const [name, setName] = React.useState("");
  const [profileSaveState, setProfileSaveState] = React.useState("idle"); // idle | saving | saved
  const [modules, setModules] = React.useState({});
  const [seatLimit, setSeatLimit] = React.useState(0);
  const [savingEnt, setSavingEnt] = React.useState(false);

  React.useEffect(() => { if (company) setName(company.name); }, [company]);
  React.useEffect(() => {
    if (entitlements) { setModules({ ...entitlements.modules }); setSeatLimit(entitlements.seat_limit); }
  }, [entitlements]);

  // Phase 8 autosave — the profile name saves on blur when it has changed.
  async function saveProfileOnBlur() {
    if (!company || name.trim() === company.name) return;
    setProfileSaveState("saving");
    try {
      await api.patch(`/companies/${companyId}`, { name: name.trim() });
      setProfileSaveState("saved");
      refresh();
      setTimeout(() => setProfileSaveState("idle"), 1400);
    } catch (e) {
      setProfileSaveState("idle");
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  async function saveEnt() {
    if (savingEnt) return;
    setSavingEnt(true);
    try {
      await api.patch(`/companies/${companyId}/entitlements`, {
        modules, seat_limit: Number(seatLimit) || 0,
      });
      toast.success("Saved");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSavingEnt(false); }
  }

  const modList = catalog?.modules || [];
  const entDirty = entitlements && (
    Number(seatLimit) !== entitlements.seat_limit ||
    JSON.stringify(modules) !== JSON.stringify(entitlements.modules)
  );

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1100px]" data-testid="settings-page">
      <div className="micro-label">Company</div>
      <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Settings</h1>

      {/* Tabs */}
      <div className="mt-5 flex gap-1 flex-wrap border-b border-brand-rule" data-testid="settings-tabs">
        {TAB_ORDER.map((t) => (
          <button key={t}
                  className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px ${
                    tab === t ? "text-brand-ink border-brand-violet" : "text-brand-muted border-transparent hover:text-brand-ink"
                  }`}
                  onClick={()=>setTab(t)} data-testid={`settings-tab-${t}`}>
            {t === "custom-fields" ? "Custom fields" : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "general" && (
          <section className="card-panel">
            <div className="card-panel-head">
              <div>
                <div className="micro-label">Profile</div>
                <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Company name</div>
              </div>
              <SaveIndicator state={profileSaveState} testid="settings-profile-save-state" />
            </div>
            <div className="card-panel-body">
              <label className="micro-label">Name</label>
              <input className="field-input mt-1 max-w-[380px]" value={name}
                     onChange={(e)=>setName(e.target.value)}
                     onBlur={canCompany ? saveProfileOnBlur : undefined}
                     onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                     disabled={!canCompany}
                     data-testid="settings-name-input" />
              {!canCompany && (
                <div className="mt-1 text-[11px] text-brand-muted">Read-only — you lack <span className="mono-num">company.manage</span>.</div>
              )}
              {canCompany && (
                <div className="mt-1 text-[10.5px] text-brand-faint">Saves automatically when you tab away.</div>
              )}
              {company?.vertical && (
                <div className="mt-4 text-[12px] text-brand-muted">
                  Current vertical: <span className="mono-num font-semibold text-brand-ink">{company.vertical}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {tab === "vertical" && <VerticalSection canCompany={canCompany} onApplied={refresh} />}
        {tab === "terminology" && <TerminologySection canCompany={canCompany} onSaved={refresh} />}
        {tab === "branding" && <BrandingSection canCompany={canCompany} />}
        {tab === "custom-fields" && <CustomFieldsSection />}
        {tab === "security" && <SecuritySection />}
        {tab === "developers" && canCompany && <DevelopersSection />}
        {tab === "developers" && !canCompany && (
          <div className="card-panel"><div className="card-panel-body text-[12px] text-brand-muted">Requires <span className="mono-num">company.manage</span>.</div></div>
        )}

        {tab === "modules" && (
          <section className="card-panel" data-testid="settings-entitlements">
            <div className="card-panel-head">
              <div>
                <div className="micro-label">Plan (simulated)</div>
                <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Modules &amp; seats</div>
              </div>
              {canEnt && (
                <button className="btn-violet" disabled={!entDirty || savingEnt} onClick={saveEnt}
                        data-testid="settings-save-entitlements">
                  <Save size={13} /> {savingEnt ? "…" : "Save"}
                </button>
              )}
            </div>
            <div className="card-panel-body space-y-5">
              <div>
                <label className="micro-label">Seat limit</label>
                <input type="number" min={1} className="field-input mt-1 max-w-[120px] mono-num"
                       value={seatLimit} onChange={(e)=>setSeatLimit(e.target.value)}
                       disabled={!canEnt} data-testid="settings-seat-limit" />
              </div>
              <div>
                <div className="micro-label mb-2">Modules</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {modList.map((m) => (
                    <label key={m.id}
                           className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-brand-rule ${canEnt ? 'hover:border-brand-violet cursor-pointer' : ''}`}
                           data-testid={`entitlement-${m.id}`}>
                      <span className="flex flex-col min-w-0">
                        <span className="text-[13px] font-semibold text-brand-ink">{m.label}</span>
                        <span className="text-[11px] text-brand-muted line-clamp-1">{m.tagline}</span>
                      </span>
                      <input type="checkbox" checked={!!modules[m.id]}
                             disabled={!canEnt}
                             onChange={() => setModules((prev) => ({ ...prev, [m.id]: !prev[m.id] }))}
                             className="h-4 w-4 accent-brand-violet"
                             data-testid={`entitlement-toggle-${m.id}`} />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ---- Vertical section
function VerticalSection({ canCompany, onApplied }) {
  const { companyId } = useParams();
  const { company } = useCompany();
  const [verticals, setVerticals] = React.useState([]);
  const [choice, setChoice] = React.useState("");
  const [applyTerms, setApplyTerms] = React.useState(true);
  const [seedExamples, setSeedExamples] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!canCompany) return;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/verticals`);
        setVerticals(data.verticals || []);
      } catch { /* noop */ }
    })();
  }, [companyId, canCompany]);

  React.useEffect(() => { if (company?.vertical) setChoice(company.vertical); }, [company]);

  async function apply() {
    if (!choice || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/apply-vertical`, {
        vertical: choice, apply_terminology: applyTerms, seed_examples: seedExamples,
      });
      toast.success(seedExamples ? `Applied — seeded ${data.seeded_services}` : "Applied");
      onApplied && onApplied();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (!canCompany) {
    return <div className="card-panel"><div className="card-panel-body text-[12px] text-brand-muted">Requires <span className="mono-num">company.manage</span>.</div></div>;
  }

  return (
    <section className="card-panel" data-testid="settings-vertical">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Industry</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
            <Sparkles size={14} /> Vertical template
          </div>
        </div>
        <button className="btn-violet" onClick={apply} disabled={busy || !choice}
                data-testid="apply-vertical">
          {busy ? "…" : "Apply"}
        </button>
      </div>
      <div className="card-panel-body space-y-4">
        <div className="text-[12px] text-brand-muted leading-relaxed">
          A vertical seeds sensible terminology defaults and (optionally) example
          services. Reapplying is safe — existing services are never duplicated.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {verticals.map((v) => (
            <label key={v.id}
                   className={`p-3 rounded-md border cursor-pointer ${
                     choice === v.id ? "border-brand-violet bg-brand-violet/5" : "border-brand-rule hover:border-brand-violet"
                   }`}
                   data-testid={`vertical-option-${v.id}`}>
              <input type="radio" name="vertical" className="hidden" checked={choice === v.id}
                     onChange={()=>setChoice(v.id)} />
              <div className="text-[13px] font-semibold text-brand-ink">{v.name}</div>
              <div className="text-[11.5px] text-brand-muted mt-0.5">{v.tagline}</div>
              <div className="mt-2 text-[10.5px] text-brand-muted">
                <span className="mono-num">{v.example_service_count}</span> example services
              </div>
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-center pt-2 border-t border-brand-rule">
          <label className="flex items-center gap-2 text-[12.5px] text-brand-ink">
            <input type="checkbox" checked={applyTerms} onChange={(e)=>setApplyTerms(e.target.checked)}
                   className="accent-brand-violet" data-testid="apply-terminology" />
            Apply terminology defaults (overwrites any manual noun overrides)
          </label>
          <label className="flex items-center gap-2 text-[12.5px] text-brand-ink">
            <input type="checkbox" checked={seedExamples} onChange={(e)=>setSeedExamples(e.target.checked)}
                   className="accent-brand-violet" data-testid="seed-examples" />
            Seed example services (skips duplicates by name)
          </label>
        </div>
      </div>
    </section>
  );
}

// ---- Terminology section
function TerminologySection({ canCompany, onSaved }) {
  const { companyId } = useParams();
  const { company } = useCompany();
  const [nouns, setNouns] = React.useState({});
  const [saveState, setSaveState] = React.useState("idle"); // idle | saving | saved
  const savedTimeout = React.useRef(null);

  React.useEffect(() => {
    setNouns({ ...(company?.terminology || {}) });
  }, [company]);

  async function saveKey(k, val) {
    // Only patch when the value has actually changed vs the server truth.
    const current = company?.terminology?.[k] || "";
    if (val === current) return;
    setSaveState("saving");
    try {
      await api.patch(`/companies/${companyId}/terminology`, { terminology: { [k]: val } });
      setSaveState("saved");
      onSaved && onSaved();
      clearTimeout(savedTimeout.current);
      savedTimeout.current = setTimeout(() => setSaveState("idle"), 1400);
    } catch (e) {
      setSaveState("idle");
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  return (
    <section className="card-panel" data-testid="settings-terminology">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Naming</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Terminology</div>
        </div>
        <SaveIndicator state={saveState} testid="settings-terminology-save-state" />
      </div>
      <div className="card-panel-body space-y-3">
        <div className="text-[12px] text-brand-muted leading-relaxed">
          Rename what nouns are displayed in the app — routes and payload
          fields never change, only the language. Saves automatically when
          you tab away.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TERM_KEYS.map((k) => (
            <label key={k.k} className="flex flex-col gap-1">
              <span className="micro-label">{k.label}</span>
              <input className="field-input mono-num" placeholder={k.k}
                     disabled={!canCompany}
                     value={nouns[k.k] || ""}
                     onChange={(e)=>setNouns((p)=>({...p, [k.k]: e.target.value}))}
                     onBlur={canCompany ? (e) => saveKey(k.k, e.target.value) : undefined}
                     onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                     data-testid={`term-${k.k}`} />
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}

// Save-state indicator — small, animated, mono. Used for autosave surfaces.
function SaveIndicator({ state, testid }) {
  if (state === "saving") {
    return (
      <span className="chip mono-num text-[10px]" data-testid={testid} data-state="saving">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-violet animate-pulse mr-1" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="chip chip-emerald mono-num text-[10px] animate-tick-in" data-testid={testid} data-state="saved">
        ✓ Saved
      </span>
    );
  }
  return null;
}

// ---- Custom fields section
function CustomFieldsSection() {
  const { companyId } = useParams();
  const [entity, setEntity] = React.useState("bookings");
  const [fields, setFields] = React.useState([]);
  const [label, setLabel] = React.useState("");
  const [type, setType] = React.useState("text");
  const [required, setRequired] = React.useState(false);
  const [options, setOptions] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/custom-fields/${entity}`);
      setFields(data.fields || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId, entity]);

  React.useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/custom-fields/${entity}`, {
        label: label.trim(), type, required,
        options: type === "select"
          ? options.split(",").map((s)=>s.trim()).filter(Boolean)
          : [],
      });
      toast.success("Created");
      setLabel(""); setOptions(""); setType("text"); setRequired(false);
      load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function del(fid) {
    if (!window.confirm("Delete this custom field? Existing values remain in place until edited.")) return;
    try {
      await api.delete(`/companies/${companyId}/custom-fields/${entity}/${fid}`);
      toast.success("Removed"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  return (
    <section className="card-panel" data-testid="settings-custom-fields">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Records</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Custom fields</div>
        </div>
        <div className="flex gap-1">
          {["bookings", "hr"].map((e) => (
            <button key={e}
                    className={`px-3 py-1.5 text-[12px] font-semibold rounded-md border ${
                      entity === e ? "bg-brand-violet text-white border-brand-violet" : "bg-card border-brand-rule text-brand-ink"
                    }`}
                    onClick={()=>setEntity(e)} data-testid={`cf-entity-${e}`}>
              {e === "bookings" ? "Bookings" : "HR"}
            </button>
          ))}
        </div>
      </div>
      <div className="card-panel-body space-y-4">
        <div className="text-[12px] text-brand-muted leading-relaxed">
          Add per-company fields to <span className="mono-num">{entity}</span>. Values persist as
          <span className="mono-num"> custom.field_key</span> on the entity document.
        </div>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2 items-start" data-testid="new-cf-form">
          <div className="flex flex-col gap-2">
            <input className="field-input" placeholder="Label (e.g. Deposit taken)"
                   value={label} onChange={(e)=>setLabel(e.target.value)} data-testid="new-cf-label" />
            {type === "select" && (
              <input className="field-input" placeholder="Options, comma separated"
                     value={options} onChange={(e)=>setOptions(e.target.value)} data-testid="new-cf-options" />
            )}
          </div>
          <select className="field-input" value={type} onChange={(e)=>setType(e.target.value)}
                  data-testid="new-cf-type">
            {["text","number","date","select"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-[12px] text-brand-muted px-2 py-2">
            <input type="checkbox" checked={required} onChange={(e)=>setRequired(e.target.checked)}
                   className="accent-brand-violet" data-testid="new-cf-required" />
            required
          </label>
          <button type="submit" className="btn-violet" disabled={busy || !label.trim()}
                  data-testid="new-cf-submit">
            <Plus size={13} /> Add
          </button>
        </form>

        <div className="fable-table">
          <table>
            <thead>
              <tr><th>Label</th><th>Key</th><th>Type</th><th>Required</th><th>Options</th><th></th></tr>
            </thead>
            <tbody>
              {fields.length === 0 && (
                <tr><td colSpan={6} className="text-center text-brand-muted py-3">No fields yet.</td></tr>
              )}
              {fields.map((f) => (
                <tr key={f.id} data-testid={`cf-row-${f.key}`}>
                  <td className="font-semibold text-brand-ink">{f.label}</td>
                  <td className="mono-num text-brand-muted">{f.key}</td>
                  <td><span className="chip mono-num">{f.type}</span></td>
                  <td>{f.required ? "yes" : "—"}</td>
                  <td className="text-brand-muted">{(f.options || []).join(", ") || "—"}</td>
                  <td className="text-right">
                    <button className="btn-ghost text-brand-coral" onClick={()=>del(f.id)}
                            data-testid={`cf-delete-${f.key}`}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Branding — Phase 15.5c (accent picker; logo PARKED)
// ─────────────────────────────────────────────────────────────
function BrandingSection({ canCompany }) {
  const { companyId } = useParams();
  const { branding: brandingCtx } = useCompany();
  const [palette, setPalette] = React.useState(null);
  const [current, setCurrent] = React.useState(null); // resolved entry (may be default)
  const [defaultKey, setDefaultKey] = React.useState("sky");
  const [saving, setSaving] = React.useState(false);
  const [hovering, setHovering] = React.useState(null); // entry being previewed

  const load = React.useCallback(async () => {
    if (!canCompany) return;
    try {
      const [{ data: pal }, { data: brand }] = await Promise.all([
        api.get(`/companies/${companyId}/branding/palette`),
        api.get(`/companies/${companyId}/branding`),
      ]);
      setPalette(pal.palette);
      setDefaultKey(pal.default_key);
      setCurrent(brand);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId, canCompany]);
  React.useEffect(() => { load(); }, [load]);

  // Hover-to-preview: paint the accent live while browsing swatches.
  React.useEffect(() => {
    if (!brandingCtx) return;
    if (hovering) brandingCtx.preview(hovering);
    else brandingCtx.reset();
  }, [hovering, brandingCtx]);

  async function pick(entry) {
    if (saving) return;
    const currentKey = current?.accent_key || (current?.is_default ? defaultKey : null);
    if (entry.key === currentKey) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/companies/${companyId}/branding`, { accent_key: entry.key });
      setCurrent(data);
      // Commit the paint through the context hook so it persists after
      // the swatch loses hover.
      if (brandingCtx?.reload) await brandingCtx.reload();
      toast.success(`Accent set to ${entry.name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  if (!canCompany) {
    return <div className="card-panel"><div className="card-panel-body text-[12px] text-brand-muted">Requires <span className="mono-num">company.manage</span>.</div></div>;
  }

  const activeKey = current?.accent_key || (current?.is_default ? defaultKey : null);

  return (
    <section className="card-panel" data-testid="settings-branding">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Appearance</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
            <Palette size={14} /> Accent
          </div>
        </div>
        {saving && (
          <span className="chip mono-num text-[10px]" data-testid="settings-branding-saving">
            <span className="inline-block h-1.5 w-1.5 rounded-full animate-pulse mr-1"
                  style={{ background: "var(--accent)" }} />
            Saving…
          </span>
        )}
      </div>
      <div className="card-panel-body space-y-5">
        <div className="text-[12px] text-brand-muted leading-relaxed max-w-[560px]">
          Pick the accent that colours buttons, links, focus rings and highlights
          across your workspace. The palette is constrained on purpose — coral,
          amber and teal are reserved for status and product-scope signals and
          can't be overridden per-company.
        </div>

        {!palette && <div className="text-[12px] text-brand-muted">Loading palette…</div>}

        {palette && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3"
                data-testid="branding-swatches">
            {palette.map((p) => {
              const isActive = p.key === activeKey;
              return (
                <button key={p.key}
                        type="button"
                        onClick={() => pick(p)}
                        onMouseEnter={() => setHovering(p)}
                        onMouseLeave={() => setHovering(null)}
                        onFocus={() => setHovering(p)}
                        onBlur={() => setHovering(null)}
                        aria-pressed={isActive}
                        className={`group relative flex flex-col items-stretch gap-2 rounded-md border p-2 transition-all ${
                          isActive
                            ? "border-2 shadow-sm"
                            : "border-brand-rule hover:border-brand-ink/30"
                        }`}
                        style={isActive ? { borderColor: p.light } : {}}
                        data-testid={`branding-swatch-${p.key}`}
                        data-active={isActive ? "true" : "false"}>
                  <div className="h-14 w-full rounded overflow-hidden flex"
                        aria-hidden="true">
                    <div className="flex-1" style={{ background: p.light }} />
                    <div className="flex-1" style={{ background: p.pressed_light }} />
                    <div className="flex-1" style={{ background: p.subtle_light }} />
                  </div>
                  <div className="flex items-center justify-between px-0.5">
                    <span className="text-[12px] font-semibold text-brand-ink">{p.name}</span>
                    {isActive && (
                      <span className="text-[9.5px] mono-num font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ background: p.subtle_light, color: p.pressed_light }}
                            data-testid={`branding-active-${p.key}`}>
                        Active
                      </span>
                    )}
                    {!isActive && p.key === defaultKey && !activeKey && (
                      <span className="text-[9.5px] mono-num text-brand-muted">default</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Live preview strip */}
        {palette && (
          <div className="rounded-md border border-brand-rule p-4 flex flex-wrap items-center gap-3"
                data-testid="branding-preview-strip">
            <span className="micro-label">Preview</span>
            <button type="button" className="btn-violet cursor-default pointer-events-none"
                    tabIndex={-1}>Primary button</button>
            <span className="chip chip-violet mono-num text-[11px]">chip · badge</span>
            <a href="#" onClick={(e) => e.preventDefault()}
                className="text-[12.5px] font-semibold underline decoration-2 underline-offset-2"
                style={{ color: "var(--accent)" }}>
              Link colour
            </a>
            <span className="ml-auto mono-num text-[10.5px] text-brand-muted">
              tokens live-swap · saves on click
            </span>
          </div>
        )}

        {/* Logo — PARKED */}
        <div className="pt-2 border-t border-brand-rule">
          <div className="flex items-start gap-3" data-testid="branding-logo-parked">
            <div className="h-9 w-9 rounded-md border border-brand-rule flex items-center justify-center text-brand-muted">
              <ImageIcon size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold text-brand-ink">Company logo</div>
                <span className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-canvas-2 text-brand-muted flex items-center gap-1">
                  <Lock size={9} /> Coming soon
                </span>
              </div>
              <div className="text-[11.5px] text-brand-muted mt-0.5 leading-relaxed">
                Logo upload lands in a later phase. Once enabled, your mark will
                render in the app header, invoice PDFs, the booking widget, and
                public Plus &amp; report pages.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

